import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';

import Button, { COLORS } from 'Components/UI/Button';
import RoundButton from 'Components/UI/RoundButton';
import IconButton from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/IconButton';
import CircularProgress from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/CircularProgress';
import { makeStyles, withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { capitalize } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/utils';

const getRootBackgroundColor = (theme, backgroundColor) => {
  return {
    '&$rootSaving.Mui-disabled': {
      '& > .MuiButton-label': {
        color: theme.palette.common.white,
      },
      backgroundColor,
    },
  };
};

const useCustomStyles = makeStyles(theme => ({
  rootSaving: {
    '&.Mui-disabled': {
      '& > .MuiButton-label *': {
        color: theme.palette.common.white,
      },
    },
  },
  rootBackgroundColorPrimary: getRootBackgroundColor(theme, theme.palette.primary.main),
  rootBackgroundColorSecondary: getRootBackgroundColor(theme, theme.palette.secondary.main),
  rootBackgroundColorAccentBlue: getRootBackgroundColor(theme, theme.palette.facet.accentBlue),
  rootBackgroundColorAccentBlue2: getRootBackgroundColor(theme, theme.palette.facet.accentBlue2),
  rootBackgroundColorDarkGray: getRootBackgroundColor(theme, theme.palette.facet.darkGray),
  rootBackgroundColorLightGray: getRootBackgroundColor(theme, theme.palette.facet.lightGray),
  rootBackgroundColorPrimaryGray: getRootBackgroundColor(theme, theme.palette.facet.primaryGray),
  rootBackgroundColorRed: getRootBackgroundColor(theme, theme.palette.facet.red),
  rootBackgroundColorOrange: getRootBackgroundColor(theme, theme.palette.facet.orange),
  rootBackgroundColorSeafoam: getRootBackgroundColor(theme, theme.palette.facet.seafoam),
}));

const styles = theme => ({
  root: {
    transition: theme.transitions.create('background-color'),
    minWidth: '150px',
  },
  rootSuccess: {
    '&.Mui-disabled': {
      '& > .MuiButton-label': {
        color: theme.palette.common.white,
      },
      backgroundColor: theme.palette.success.main,
    },
  },
  rootError: {
    '&.Mui-disabled': {
      '& > .MuiButton-label': {
        color: theme.palette.common.white,
      },
      backgroundColor: theme.palette.error.main,
    },
  },
  successIcon: {
    color: theme.palette.success.main,
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
});

const SubmitButton = ({
  className,
  classes,
  color,
  errorDuration,
  errorIcon,
  errorText,
  onClick,
  rounded,
  savingText,
  successDuration,
  successIcon,
  successText,
  text,
  disabled,
  IconButtonProps,
  ...rest
}) => {
  const customClasses = useCustomStyles();
  const [isSaving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const ButtonComponent = (rounded) ? RoundButton : Button;

  useEffect(() => {
    if (result !== null) {
      const timeoutDuration = (result === 'success') ? successDuration : errorDuration;
      setTimeout(() => {
        setResult(null);
      }, timeoutDuration);
    }
  }, [result]);

  let buttonText;
  let IconButtonIcon;
  let StartIcon = null;
  let startIconProps = {};

  switch (result) {
    case 'success':
      buttonText = successText;
      StartIcon = successIcon;
      break;
    case 'error':
      buttonText = errorText;
      StartIcon = errorIcon;
      break;
    default:
      if (isSaving) {
        buttonText = savingText;
        StartIcon = CircularProgress;
        startIconProps = {
          style: {
            width: 20,
            height: 20,
          },
        };
      } else {
        buttonText = text;
      }
      break;
  }

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onClick({
        onSuccess: () => setResult('success'),
        onFailure: () => setResult('error'),
      });
    } catch {
      setResult('error');
    }
    setSaving(false);
  };

  return IconButtonProps ? (
    <div
      className={classNames(className, customClasses[`rootBackgroundColor${capitalize(color)}`], {
        [customClasses.rootSaving]: isSaving,
        [classes.rootSuccess]: result === 'success',
        [classes.rootError]: result === 'error',
      })}
    >
      {!isSaving && !result && (
        <IconButton
          disabled={disabled || isSaving || result !== null}
          onClick={() => handleSubmit()}
          icon={IconButtonIcon}
          {...IconButtonProps}
          {...rest}
        />)
      }
      {!result && isSaving && <CircularProgress size={25} />}
      {result === 'success' && <CheckCircleIcon className={classes.successIcon} />}
      {result === 'error' && <ErrorIcon className={classes.errorIcon} />}
    </div>
  ) : (
    <ButtonComponent
      className={classNames(classes.root, className, customClasses[`rootBackgroundColor${capitalize(color)}`], {
        [customClasses.rootSaving]: isSaving,
        [classes.rootSuccess]: result === 'success',
        [classes.rootError]: result === 'error',
      })}
      color={color}
      onClick={() => handleSubmit()}
      startIcon={(StartIcon) ? <StartIcon {...startIconProps} /> : null}
      {...rest}
      disabled={disabled || isSaving || result !== null}
    >
      {buttonText}
    </ButtonComponent>
  );
};

SubmitButton.defaultProps = {
  className: '',
  color: 'primary',
  errorDuration: 3000,
  errorIcon: ErrorIcon,
  errorText: 'Save Failed',
  rounded: false,
  savingText: 'Saving',
  successIcon: CheckCircleIcon,
  successText: 'Save Successful',
  successDuration: 3000,
  text: 'Save',
  disabled: false,
  IconButtonProps: null,
};

SubmitButton.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  color: PropTypes.oneOf(COLORS),
  errorDuration: PropTypes.number,
  errorIcon: PropTypes.object,
  errorText: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  rounded: PropTypes.bool,
  savingText: PropTypes.string,
  successDuration: PropTypes.number,
  successIcon: PropTypes.object,
  successText: PropTypes.string,
  text: PropTypes.string,
  disabled: PropTypes.bool,
  IconButtonProps: PropTypes.object,
};

export default withStyles(styles)(SubmitButton);
